import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { valueRender } from '@repo/toolkit/conditional-render'
import { DateFormatter } from '@repo/toolkit/date'
import { AvailableLanguages } from '@repo/toolkit/language-types'
import Badge from '@repo/ui/badge'
import { Column } from '@repo/ui/data-table'
import { PharmacyIcon } from '@repo/ui/icons'
import StatusIndicator from '@repo/ui/status-indicator'
import TextLink from '@repo/ui/text-link'
import Typography from '@repo/ui/typography'
import { TFunction } from 'i18next'

import i18n from '../../../../../i18n'
import { Paths } from '../../../../lib/constants/path'
import { AccomodationEnum } from '../../../../lib/types/enums/accomodation-type/accomodation-type'
import { genderOneLetterMapping } from '../../../../lib/types/enums/common/gender.enum'
import { schoolLevelEnumMapping } from '../../../../lib/types/enums/common/school-level.enum'
import { MedicalConditionEnum } from '../../../../lib/types/enums/enrollment/medical-condition-enum'
import { familyArrangementEnumMapping } from '../../../../lib/types/enums/ol/family-arrangement.enum'
import { OLTypeEnum } from '../../../../lib/types/enums/ol/ol-type.enum'
import { RoleEnum } from '../../../../lib/types/enums/users/role.enum'
import { EnrollmentProperties } from '../../../../lib/types/models/enrollment/enrollment.properties'
import {
  EnrollmentCenter,
  EnrollmentDiffusionStateEnum
} from '../../../../lib/types/models/enrollment-center/enrollment-center'
import { NestedColumnActionMap } from '../../../../lib/types/utils'
import { getTranslatedName } from '../../../../lib/utils/functions/translation.functions'
import AccomodationTooltip from '../../../elements/accomodation-tooltip'
import InformationToolTip from '../../../elements/information-tooltip'
import AssignCenterGroupCell from '../assign-center-group-cell'
import OlCenterEnrollmentTableFriendsTooltip from './ol-center-enrollment-table-friends-tooltip'
import OlCenterEnrollmentTableHostFamily from './ol-center-enrollment-table-host-family-tooltip'
import OlCenterEnrollmentTableJourneyInfo from './ol-center-enrollment-table-journey-info'
import OlCenterEnrollmentTableStudentInfo from './ol-center-enrollment-table-student-info'

type EnrollmentColumn<T = EnrollmentCenter> = Column<T> & {
  variant: { role: RoleEnum; order: number }[]
  accomodations: AccomodationEnum[]
  olTypes: OLTypeEnum[]
}

type EnrollmentColumnData = {
  isOl?: boolean
  newEnrollmentIds?: number[]
  t: TFunction<AvailableLanguages, undefined>
  actions?: NestedColumnActionMap<EnrollmentCenter>
  onNameClick?: (enrollment: EnrollmentCenter) => void
  onCenterGroupChange?: () => void
}

const enrollmentColumns = ({
  t,
  ...data
}: EnrollmentColumnData): EnrollmentColumn<EnrollmentCenter>[] => [
  {
    accessorKey: 'hostFamilyDiffused',
    accomodations: [AccomodationEnum.HOST_FAMILY],
    variant: [
      { role: RoleEnum.ADMIN, order: 1 },
      { role: RoleEnum.Ol, order: 1 }
    ],
    olTypes: [OLTypeEnum.LEC, OLTypeEnum.PARTNER],
    header: t('ol.center.enrollment.table.columns.student.placed'),
    cell: ({ row }) => {
      const hasHostFamily = data.isOl ? row.original.hostFamily : row.original.hostFamilyDiffused
      return <StatusIndicator status={hasHostFamily ? 'active' : 'inactive'} />
    }
  },
  {
    accessorKey: 'hostFamilyDiffusedAt',
    header: t('ol.center.enrollment.table.columns.diffusion.date.parents'),
    enableSorting: true,
    accomodations: [
      AccomodationEnum.HOST_FAMILY,
      AccomodationEnum.COLLEGE,
      AccomodationEnum.CAMPUS,
      AccomodationEnum.RESIDENCE,
      AccomodationEnum.HOTEL
    ],
    olTypes: [OLTypeEnum.LEC, OLTypeEnum.PARTNER, OLTypeEnum.RESIDENCE],
    variant: [{ role: RoleEnum.ADMIN, order: 2 }],
    cell: ({ row }) => {
      if (!row.original.hostFamilyDiffusedAt) return '-'
      return <Badge text={DateFormatter.DDMMYYYY(row.original.hostFamilyDiffusedAt)} />
    }
  },
  {
    accessorKey: 'enrollment.childLastName',
    header: t('ol.center.enrollment.table.columns.lastName'),
    enableSorting: true,
    accomodations: [
      AccomodationEnum.HOST_FAMILY,
      AccomodationEnum.COLLEGE,
      AccomodationEnum.CAMPUS,
      AccomodationEnum.RESIDENCE,
      AccomodationEnum.HOTEL
    ],
    variant: [{ role: RoleEnum.ADMIN, order: 5 }],
    olTypes: [OLTypeEnum.LEC, OLTypeEnum.PARTNER, OLTypeEnum.RESIDENCE],
    cell: ({ row }) => (
      <TextLink
        href={Paths.ENROLLMENT_PROFILE_PAGE.replace(':id', row.original.enrollmentId.toString())}
        name={valueRender(row.original.enrollment.childLastName?.toUpperCase())}
      />
    )
  },
  {
    accessorKey: 'enrollment.childLastName',
    header: t('ol.center.enrollment.table.columns.lastName'),
    enableSorting: true,
    accomodations: [
      AccomodationEnum.HOST_FAMILY,
      AccomodationEnum.COLLEGE,
      AccomodationEnum.CAMPUS,
      AccomodationEnum.RESIDENCE,
      AccomodationEnum.HOTEL
    ],
    olTypes: [OLTypeEnum.LEC, OLTypeEnum.PARTNER, OLTypeEnum.RESIDENCE],
    variant: [{ role: RoleEnum.Ol, order: 2 }],
    cell: ({ row }) => {
      const { diffusionState, enrollmentId } = row.original
      const state =
        diffusionState === EnrollmentDiffusionStateEnum.DELETED
          ? 'deleted'
          : data.newEnrollmentIds?.includes(enrollmentId)
            ? 'added'
            : 'normal'
      return (
        <div className="flex flex-col gap-1 ">
          <Typography.Link
            variant="label-semibold"
            onClick={() => data.onNameClick?.(row.original)}
          >
            {valueRender(row.original.enrollment.childLastName?.toUpperCase())}
          </Typography.Link>

          {state !== 'normal' && (
            <Typography
              variant="label"
              color={state === 'added' ? tailwindColors.green.DEFAULT : tailwindColors.red.DEFAULT}
            >
              {state === 'added' ? '(Recently Added)' : '(Removed)'}
            </Typography>
          )}
        </div>
      )
    }
  },
  {
    accessorKey: 'enrollment.childFirstName',
    enableSorting: true,
    accomodations: [
      AccomodationEnum.HOST_FAMILY,
      AccomodationEnum.COLLEGE,
      AccomodationEnum.CAMPUS,
      AccomodationEnum.RESIDENCE,
      AccomodationEnum.HOTEL
    ],
    variant: [
      { role: RoleEnum.Ol, order: 3 },
      { role: RoleEnum.ADMIN, order: 6 }
    ],
    olTypes: [OLTypeEnum.LEC, OLTypeEnum.PARTNER, OLTypeEnum.RESIDENCE],
    header: t('ol.center.enrollment.table.columns.firstName'),
    cell: ({ row }) => <OlCenterEnrollmentTableStudentInfo enrollment={row.original.enrollment} />
  },
  {
    accessorKey: 'enrollment.childBirthDate',
    header: t('ol.center.enrollment.table.columns.age'),
    accomodations: [
      AccomodationEnum.HOST_FAMILY,
      AccomodationEnum.COLLEGE,
      AccomodationEnum.CAMPUS,
      AccomodationEnum.RESIDENCE,
      AccomodationEnum.HOTEL
    ],
    variant: [
      { role: RoleEnum.Ol, order: 4 },
      { role: RoleEnum.ADMIN, order: 7 }
    ],
    olTypes: [OLTypeEnum.LEC, OLTypeEnum.PARTNER, OLTypeEnum.RESIDENCE],
    enableSorting: true,
    cell: ({ row }) => EnrollmentProperties.getAgeAtDeparture(row.original.enrollment) ?? '-'
  },
  {
    accessorKey: 'enrollment.childSchoolLevel',
    header: 'Classe',
    accomodations: [
      AccomodationEnum.HOST_FAMILY,
      AccomodationEnum.COLLEGE,
      AccomodationEnum.CAMPUS,
      AccomodationEnum.RESIDENCE,
      AccomodationEnum.HOTEL
    ],
    variant: [{ role: RoleEnum.ADMIN, order: 8 }],
    olTypes: [OLTypeEnum.LEC, OLTypeEnum.PARTNER, OLTypeEnum.RESIDENCE],
    enableSorting: true,
    cell: ({ row }) =>
      valueRender(
        row.original.enrollment.childSchoolLevel,
        (level) => schoolLevelEnumMapping[level]
      )
  },
  {
    accessorKey: 'enrollment.childGender',
    enableSorting: true,
    header: t('ol.center.enrollment.table.columns.gender'),
    accomodations: [
      AccomodationEnum.HOST_FAMILY,
      AccomodationEnum.COLLEGE,
      AccomodationEnum.CAMPUS,
      AccomodationEnum.RESIDENCE,
      AccomodationEnum.HOTEL
    ],
    variant: [
      { role: RoleEnum.Ol, order: 5 },
      { role: RoleEnum.ADMIN, order: 9 }
    ],
    olTypes: [OLTypeEnum.LEC, OLTypeEnum.PARTNER, OLTypeEnum.RESIDENCE],
    cell: ({ row }) =>
      valueRender(
        row.original.enrollment.childGender,
        (gender) => genderOneLetterMapping[gender].label
      )
  },
  {
    accessorKey: 'enrollment.medicalStatus',
    header: t('ol.center.enrollment.table.columns.medicalStatus'),
    accomodations: [
      AccomodationEnum.HOST_FAMILY,
      AccomodationEnum.COLLEGE,
      AccomodationEnum.CAMPUS,
      AccomodationEnum.RESIDENCE,
      AccomodationEnum.HOTEL
    ],
    olTypes: [OLTypeEnum.LEC, OLTypeEnum.PARTNER, OLTypeEnum.RESIDENCE],
    variant: [
      { role: RoleEnum.Ol, order: 6 },
      { role: RoleEnum.ADMIN, order: 10 }
    ],
    cell: ({ row }) => {
      const enrollment = row.original.enrollment
      if (enrollment.medicalLevel !== MedicalConditionEnum.RAS)
        return (
          <InformationToolTip
            icon={
              <Badge
                variant={'success'}
                icon={
                  <PharmacyIcon
                    color={tailwindColors.green.DEFAULT}
                    color2={tailwindColors.green.light.DEFAULT}
                  />
                }
              />
            }
            contentVariant="secondary"
          >
            <Typography variant={'label-medium'} color={tailwindColors.black.DEFAULT}>
              {enrollment.medicalInformationsEn}
            </Typography>
            <div className="h-1" />
            <Typography variant={'label-medium'} color={tailwindColors.black.DEFAULT}>
              {enrollment.additionalMedicalInformationsEn}
            </Typography>
          </InformationToolTip>
        )
      return <div className="flex items-center justify-center">-</div>
    }
  },
  {
    accessorKey: 'enrollment.mainProposition.familyArrangement',
    header: t('ol.center.enrollment.table.columns.familyArrangement'),
    accomodations: [
      AccomodationEnum.HOST_FAMILY,
      AccomodationEnum.COLLEGE,
      AccomodationEnum.CAMPUS,
      AccomodationEnum.RESIDENCE,
      AccomodationEnum.HOTEL
    ],
    variant: [
      { role: RoleEnum.Ol, order: 7 },
      { role: RoleEnum.ADMIN, order: 4 }
    ],
    olTypes: [OLTypeEnum.LEC, OLTypeEnum.PARTNER],
    cellClassName: 'justify-center',
    cell: ({ row }) => {
      const familyArrangement = row.original.enrollment.mainProposition?.familyArrangement
      if (!familyArrangement) return '-'
      // the local organiser reads the choice in words, LEC keeps the icon
      if (data.isOl) return t(familyArrangementEnumMapping[familyArrangement].olLabel)
      return <AccomodationTooltip familyArrangement={familyArrangement} />
    }
  },
  {
    accessorKey: 'enrollment.declaredFriends',
    accomodations: [
      AccomodationEnum.HOST_FAMILY,
      AccomodationEnum.COLLEGE,
      AccomodationEnum.CAMPUS,
      AccomodationEnum.RESIDENCE,
      AccomodationEnum.HOTEL
    ],
    variant: [
      { role: RoleEnum.Ol, order: 8 },
      { role: RoleEnum.ADMIN, order: 11 }
    ],
    olTypes: [OLTypeEnum.LEC, OLTypeEnum.PARTNER, OLTypeEnum.RESIDENCE],
    header: t('ol.center.enrollment.table.columns.friends'),
    cell: ({ row }) => (
      <OlCenterEnrollmentTableFriendsTooltip friends={row.original.enrollment.declaredFriends} />
    )
  },
  {
    accessorKey: 'centerGroup.name',
    enableSorting: true,
    accomodations: [
      AccomodationEnum.HOST_FAMILY,
      AccomodationEnum.COLLEGE,
      AccomodationEnum.CAMPUS,
      AccomodationEnum.RESIDENCE,
      AccomodationEnum.HOTEL
    ],
    variant: [
      { role: RoleEnum.Ol, order: 9 },
      { role: RoleEnum.ADMIN, order: 12 }
    ],
    olTypes: [OLTypeEnum.LEC, OLTypeEnum.PARTNER, OLTypeEnum.RESIDENCE],
    header: t('ol.center.enrollment.table.columns.center.group'),
    cell: ({ row }) => (
      <AssignCenterGroupCell
        permissions={data.isOl ? [] : ['ASSIGN_CENTER_GROUP', 'UNASSIGN_CENTER_GROUP']}
        enrollmentCenter={row.original}
        onSuccess={data.onCenterGroupChange}
      />
    )
  },
  {
    accessorKey: 'hostFamily.parent1LastName',
    header: t('ol.center.enrollment.table.columns.host.family'),
    accomodations: [AccomodationEnum.HOST_FAMILY],
    variant: [
      { role: RoleEnum.Ol, order: 10 },
      { role: RoleEnum.ADMIN, order: 3 }
    ],
    olTypes: [OLTypeEnum.LEC, OLTypeEnum.PARTNER],
    enableSorting: true,
    cell: ({ row }) => {
      const hasHostFamily = !!row.original.hostFamily
      return (
        <OlCenterEnrollmentTableHostFamily
          className="py-2"
          enrollmentCenter={row.original}
          buttonProps={{
            label: hasHostFamily ? 'Change host family' : 'Assign host family',
            variant: hasHostFamily ? 'secondary' : 'ghost',
            onClick: () => data.actions?.hostFamily?.parent1LastName?.(row.original)
          }}
        />
      )
    }
  },
  {
    accessorKey: 'hostFamilyDiffused',
    header: t('ol.center.enrollment.table.columns.host.family.info.sent.to.parents'),
    accomodations: [AccomodationEnum.HOST_FAMILY],
    variant: [{ role: RoleEnum.Ol, order: 11 }],
    olTypes: [OLTypeEnum.LEC, OLTypeEnum.PARTNER],
    cell: ({ row }) => (
      <StatusIndicator status={row.original.hostFamilyDiffused ? 'active' : 'inactive'} />
    )
  },
  {
    accessorKey: 'hostFamilyDiffused',
    header: t('ol.center.enrollment.table.columns.host.family.info.sent.by.lec.to.the.parents'),
    accomodations: [
      AccomodationEnum.HOST_FAMILY,
      AccomodationEnum.COLLEGE,
      AccomodationEnum.CAMPUS,
      AccomodationEnum.RESIDENCE,
      AccomodationEnum.HOTEL
    ],
    variant: [{ role: RoleEnum.Ol, order: 11 }],
    olTypes: [OLTypeEnum.RESIDENCE],
    cell: ({ row }) => (
      <StatusIndicator status={row.original.hostFamilyDiffused ? 'active' : 'inactive'} />
    )
  },
  {
    accessorKey: 'enrollment.mainProposition.journey',
    header: t('ol.center.enrollment.table.columns.journeys'),
    accomodations: [
      AccomodationEnum.HOST_FAMILY,
      AccomodationEnum.COLLEGE,
      AccomodationEnum.CAMPUS,
      AccomodationEnum.RESIDENCE,
      AccomodationEnum.HOTEL
    ],
    variant: [
      { role: RoleEnum.Ol, order: 12 },
      { role: RoleEnum.ADMIN, order: 13 }
    ],
    olTypes: [OLTypeEnum.LEC, OLTypeEnum.PARTNER, OLTypeEnum.RESIDENCE],
    cellClassName: 'py-2',
    cell: ({ row }) => {
      const journey = row.original.enrollment.mainProposition?.journey
      if (!journey) return '-'
      return <OlCenterEnrollmentTableJourneyInfo journey={journey} />
    }
  },
  {
    accessorKey: 'diffusedAt',
    header: t('ol.center.enrollment.table.columns.option'),
    accomodations: [
      AccomodationEnum.HOST_FAMILY,
      AccomodationEnum.COLLEGE,
      AccomodationEnum.CAMPUS,
      AccomodationEnum.RESIDENCE,
      AccomodationEnum.HOTEL
    ],
    variant: [
      { role: RoleEnum.Ol, order: 13 },
      { role: RoleEnum.ADMIN, order: 14 }
    ],
    olTypes: [OLTypeEnum.LEC, OLTypeEnum.PARTNER, OLTypeEnum.RESIDENCE],
    cell: ({ row }) => {
      const options = row.original.enrollment.mainProposition.options
      if (!options || options.length === 0) return '-'

      return options
        .map(({ option }) => getTranslatedName(option, i18n.language as AvailableLanguages) || '—')
        .join(', ')
    }
  },
  {
    accessorKey: 'diffusedAt',
    header: t('ol.center.enrollment.table.columns.diffusion.date.presta'),
    accomodations: [
      AccomodationEnum.HOST_FAMILY,
      AccomodationEnum.COLLEGE,
      AccomodationEnum.CAMPUS,
      AccomodationEnum.RESIDENCE,
      AccomodationEnum.HOTEL
    ],
    variant: [
      { role: RoleEnum.Ol, order: 14 },
      { role: RoleEnum.ADMIN, order: 15 }
    ],
    olTypes: [OLTypeEnum.LEC, OLTypeEnum.PARTNER, OLTypeEnum.RESIDENCE],
    cell: ({ row }) =>
      row.original.diffusedAt ? (
        <Badge text={DateFormatter.DDMMYYYY(row.original.diffusedAt)} />
      ) : (
        '-'
      )
  }
]

type GetEnrollmentColumnsOptions = {
  olType?: OLTypeEnum
  userRoles: RoleEnum[]
  accomodation: AccomodationEnum
} & Omit<EnrollmentColumnData, 'isOl'>

export const getEnrollmentColumns = (
  data: GetEnrollmentColumnsOptions
): Column<EnrollmentCenter>[] => {
  const { userRoles, accomodation, olType, ...rest } = data
  return enrollmentColumns({ ...rest, isOl: userRoles.includes(RoleEnum.Ol) })
    .filter(
      (column) =>
        !column.variant || column.variant.some((variant) => userRoles.includes(variant.role))
    )
    .filter((column) => column.accomodations.includes(accomodation))
    .filter((column) => !olType || column.olTypes.includes(olType))
    .sort((a, b) => {
      const aOrder = a.variant?.find((v) => userRoles.includes(v.role))?.order ?? 0
      const bOrder = b.variant?.find((v) => userRoles.includes(v.role))?.order ?? 0
      return aOrder - bOrder
    })
}
